<template>
  <div class="student-dashboard">
    <!-- Header -->
    <v-card class="mb-4 gradient-header">
      <v-card-text class="pa-6">
        <div class="d-flex justify-space-between align-center">
          <div>
            <h1 class="text-h4 font-weight-bold text-white mb-2">
              📊 Dashboard นักเรียนสาขาวิทยาศาสตร์และเทคโนโลยี
            </h1>
            <p class="text-subtitle1 text-white-70 mb-0">
              ข้อมูลการลงทะเบียนนักเรียนตามภูมิภาคและสาขาวิชา
            </p>
          </div>
          <v-btn 
            icon 
            large 
            @click="refreshData"
            :loading="loading"
            class="white--text"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="6000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Filter Section -->
    <v-card class="mb-4">
      <v-card-title class="font-weight-bold">
        <v-icon left>mdi-filter</v-icon>
        ตัวกรองข้อมูล
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12" sm="4" class="pb-0 pb-sm-3">
            <v-select
              v-model="filters.year"
              :items="['2568', '2567']"
              label="ปีการศึกษา"
              outlined
              dense
              clearable
              @input="applyFilters"
              prepend-icon="mdi-calendar"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0 pb-sm-3">
            <v-select
              v-model="filters.region"
              :items="regions"
              label="ภาค"
              outlined
              dense
              clearable
              @input="applyFilters"
              prepend-icon="mdi-map"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0 pb-sm-3">
            <v-select
              v-model="filters.branch"
              :items="branches"
              label="สาขาวิชา"
              outlined
              dense
              clearable
              @input="applyFilters"
              prepend-icon="mdi-book-multiple"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Key Statistics -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-1">
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-overline text-grey mb-2">จำนวนนักเรียนทั้งหมด</p>
                <h2 class="text-h3 font-weight-bold text-gradient-1">
                  {{ stats.total_students | formatNumber }}
                </h2>
              </div>
              <v-icon size="60" class="icon-stat icon-stat-1">mdi-account-multiple</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-2">
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-overline text-grey mb-2">สถาบันการศึกษา</p>
                <h2 class="text-h3 font-weight-bold text-gradient-2">
                  {{ stats.total_colleges | formatNumber }}
                </h2>
              </div>
              <v-icon size="60" class="icon-stat icon-stat-2">mdi-school</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-3">
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-overline text-grey mb-2">ภูมิภาค</p>
                <h2 class="text-h3 font-weight-bold text-gradient-3">
                  {{ stats.total_regions | formatNumber }}
                </h2>
              </div>
              <v-icon size="60" class="icon-stat icon-stat-3">mdi-map-marker-multiple</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-4">
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-overline text-grey mb-2">สาขาวิชา</p>
                <h2 class="text-h3 font-weight-bold text-gradient-4">
                  {{ stats.total_branches | formatNumber }}
                </h2>
              </div>
              <v-icon size="60" class="icon-stat icon-stat-4">mdi-book-multiple-variant</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section -->
    <v-row class="mb-4">
      <v-col cols="12" lg="8">
        <v-card>
          <v-card-title class="font-weight-bold">
            <v-icon left>mdi-chart-bar</v-icon>
            จำนวนนักเรียนตามภาค
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <apexchart
              v-if="chartByRegionSeries.length"
              type="bar"
              :options="chartByRegionOptions"
              :series="chartByRegionSeries"
              height="400"
            ></apexchart>
            <div v-else class="pa-6 text-center text-grey">
              ไม่มีข้อมูลแสดง
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card>
          <v-card-title class="font-weight-bold">
            <v-icon left>mdi-chart-pie</v-icon>
            สัดส่วนนักเรียนตามภาค
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <apexchart
              v-if="chartPieSeries.length"
              type="pie"
              :options="chartPieOptions"
              :series="chartPieSeries"
              height="400"
            ></apexchart>
            <div v-else class="pa-6 text-center text-grey">
              ไม่มีข้อมูลแสดง
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Regional Map Section -->
    <v-card class="mb-4">
      <v-card-title class="font-weight-bold">
        <v-icon left>mdi-map</v-icon>
        แผนที่ประเทศไทย - จำนวนนักเรียนตามภาค
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-6">
        <thailand-map :data="mapData"></thailand-map>
      </v-card-text>
    </v-card>

    <!-- Top Branches Section -->
    <v-row class="mb-4">
      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title class="font-weight-bold">
            <v-icon left>mdi-book-multiple</v-icon>
            สาขาวิชายอดนิยม (Top 5)
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4">
            <apexchart
              v-if="chartTopBranchesSeries.length"
              type="bar"
              :options="chartTopBranchesOptions"
              :series="chartTopBranchesSeries"
              height="350"
            ></apexchart>
            <div v-else class="pa-6 text-center text-grey">
              ไม่มีข้อมูลแสดง
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title class="font-weight-bold">
            <v-icon left>mdi-school</v-icon>
            สถาบันชั้นนำ (Top 5)
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4">
            <v-list subheader v-if="topColleges.length">
              <v-subheader>สถาบันการศึกษาที่มีจำนวนนักเรียนมากที่สุด</v-subheader>
              <v-list-item
                v-for="(item, index) in topColleges"
                :key="index"
                class="mb-2"
              >
                <v-list-item-avatar
                  color="primary"
                  class="font-weight-bold text-white"
                >
                  {{ index + 1 }}
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">
                    {{ item.college_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.region_name }} | นักเรียน: {{ toNumber(item.std_num) | formatNumber }} คน
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <div v-else class="pa-6 text-center text-grey">
              ไม่มีข้อมูลแสดง
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detailed Table -->
    <v-card>
      <v-card-title class="font-weight-bold">
        <v-icon left>mdi-table</v-icon>
        ข้อมูลรายละเอียด
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <v-data-table
          :headers="tableHeaders"
          :items="tableData"
          :loading="loading"
          class="elevation-1"
          :items-per-page="15"
          :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }"
        >
          <template v-slot:item.std_num="{ item }">
            <v-chip
              :color="getStudentCountColor(toNumber(item.std_num))"
              text-color="white"
              label
              small
            >
              {{ toNumber(item.std_num) | formatNumber }} คน
            </v-chip>
          </template>

          <template v-slot:item.region_name="{ item }">
            <v-chip
              :color="getRegionColor(item.region_name)"
              text-color="white"
              small
            >
              {{ item.region_name }}
            </v-chip>
          </template>

          <template v-slot:item.update_time="{ item }">
            {{ formatDate(item.update_time) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import ThailandMap from './ThailandMap.vue';

export default {
  name: 'StudentDashboard',
  components: {
    apexchart: VueApexCharts,
    ThailandMap
  },
  data() {
    return {
      loading: false,
      snackbar: {
        show: false,
        message: '',
        color: 'error'
      },
      filters: {
        year: '2568',
        region: null,
        branch: null
      },
      tableData: [],
      regions: [],
      branches: [],
      stats: {
        total_students: 0,
        total_colleges: 0,
        total_regions: 0,
        total_branches: 0
      },
      topColleges: [],
      mapData: {},
      
      // Chart Options
      chartByRegionOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: true },
          fontFamily: '"Roboto", sans-serif'
        },
        colors: ['#1976D2', '#1565C0', '#1E88E5', '#2196F3', '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 8
          }
        },
        xaxis: {
          title: { text: 'ภาค' },
          categories: [],
          style: { fontSize: '12px' }
        },
        yaxis: {
          title: { text: 'จำนวนนักเรียน' }
        },
        dataLabels: { enabled: true }
      },
      chartByRegionSeries: [],

      chartPieOptions: {
        chart: {
          type: 'pie',
          fontFamily: '"Roboto", sans-serif'
        },
        colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'],
        legend: { position: 'bottom' },
        labels: [],
        dataLabels: { enabled: true }
      },
      chartPieSeries: [],

      chartTopBranchesOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: '"Roboto", sans-serif'
        },
        colors: ['#4CAF50'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          title: { text: 'จำนวนนักเรียน' },
          categories: []
        },
        dataLabels: { enabled: true }
      },
      chartTopBranchesSeries: [],

      tableHeaders: [
        { text: 'สถาบันการศึกษา', value: 'college_name', width: '25%' },
        { text: 'สาขาวิชา', value: 'branch_name_th', width: '20%' },
        { text: 'ภาค', value: 'region_name', width: '15%' },
        { text: 'จำนวนนักเรียน', value: 'std_num', width: '12%' },
        { text: 'ชั้นปี', value: 'rate_work_college_class', width: '12%' },
        { text: 'วันที่อัพเดท', value: 'update_time', width: '16%' }
      ]
    };
  },

  filters: {
    formatNumber(value) {
      if (value === null || value === undefined) return '0';
      const num = parseInt(value, 10) || 0;
      return num.toLocaleString('th-TH');
    }
  },

  mounted() {
    this.loadInitialData();
  },

  methods: {
    /**
     * ✅ Convert String to Number safely
     * Prevents string concatenation instead of addition
     */
    toNumber(value) {
      if (value === null || value === undefined || value === '') return 0;
      const num = parseInt(String(value), 10) || 0;
      return Math.max(0, num); // Prevent negative values
    },

    /**
     * ✅ FIXED: Proper error handling with Promise.allSettled
     */
    async loadInitialData() {
      this.loading = true;
      const errors = [];
      
      try {
        // Use allSettled to prevent one failure from blocking others
        const results = await Promise.allSettled([
          this.fetchRegions(),
          this.fetchBranches(),
          this.fetchStats(),
          this.applyFilters()
        ]);

        // Check which operations failed
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const operations = ['regions', 'branches', 'statistics', 'table data'];
            errors.push(`Failed to load ${operations[index]}`);
          }
        });

        if (errors.length > 0 && errors.length < 4) {
          // Partial failure - show warning
          this.showNotification(
            `⚠️ บางข้อมูลล้มเหลว: ${errors.join(', ')}`,
            'warning'
          );
        } else if (errors.length === 4) {
          // Complete failure
          this.showNotification(
            '❌ ไม่สามารถโหลดข้อมูลได้ กรุณาตรวจสอบการเชื่อมต่อ',
            'error'
          );
        }
      } catch (error) {
        console.error('Critical error:', error);
        this.showNotification('❌ เกิดข้อผิดพลาดร้ายแรง', 'error');
      } finally {
        this.loading = false;
      }
    },

    /**
     * ✅ FIXED: Proper API error handling
     */
    async fetchRegions() {
      try {
        const data = await this.apiCall('get_regions', {});
        this.regions = data.data || [];
        console.log('✅ Regions loaded:', this.regions);
      } catch (error) {
        console.error('❌ Error fetching regions:', error);
        throw error;
      }
    },

    async fetchBranches() {
      try {
        const data = await this.apiCall('get_branches', {});
        this.branches = data.data || [];
        console.log('✅ Branches loaded:', this.branches);
      } catch (error) {
        console.error('❌ Error fetching branches:', error);
        throw error;
      }
    },

    async fetchStats() {
      try {
        const data = await this.apiCall('get_stats', {
          rate_work_college_year: this.filters.year
        });
        
        if (data.summary) {
          // ✅ FIXED: Convert stats to Numbers
          this.stats = {
            total_students: this.toNumber(data.summary.total_students),
            total_colleges: this.toNumber(data.summary.total_colleges),
            total_regions: this.toNumber(data.summary.total_regions),
            total_branches: this.toNumber(data.summary.total_branches)
          };
        }

        this.processStatsForCharts(data.data);
        console.log('✅ Statistics loaded:', this.stats);
      } catch (error) {
        console.error('❌ Error fetching stats:', error);
        throw error;
      }
    },

    async applyFilters() {
      this.loading = true;
      try {
        const data = await this.apiCall('get_all', {
          rate_work_college_year: this.filters.year,
          region_name: this.filters.region,
          rate_work_college_class: this.filters.branch
        });

        // ✅ FIXED: Convert all std_num to Numbers
        this.tableData = (data.data || []).map(item => ({
          ...item,
          std_num: this.toNumber(item.std_num)
        }));

        this.generateCharts();
        this.generateMapData();
        this.generateTopColleges();
        console.log('✅ Table data loaded:', this.tableData.length, 'rows');
      } catch (error) {
        console.error('❌ Error applying filters:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * ✅ FIXED: Create charts with correct Numbers
     */
    generateCharts() {
      // Group by region - Sum as Numbers (not string concatenation)
      const regionMap = {};
      this.tableData.forEach(item => {
        const region = item.region_name || 'ไม่ระบุ';
        const count = this.toNumber(item.std_num);
        regionMap[region] = (regionMap[region] || 0) + count;
      });

      const regions = Object.keys(regionMap).sort();
      const values = regions.map(r => regionMap[r]);

      // Bar Chart
      this.chartByRegionSeries = [{
        name: 'จำนวนนักเรียน',
        data: values
      }];

      this.$set(this.chartByRegionOptions.xaxis, 'categories', regions);

      // Pie Chart
      this.chartPieSeries = [...values];
      this.$set(this.chartPieOptions, 'labels', regions);

      // Top Branches - Sum as Numbers
      const branchMap = {};
      this.tableData.forEach(item => {
        const branch = item.branch_name_th || 'ไม่ระบุ';
        const count = this.toNumber(item.std_num);
        branchMap[branch] = (branchMap[branch] || 0) + count;
      });

      const topBranches = Object.entries(branchMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      this.chartTopBranchesSeries = [{
        name: 'จำนวนนักเรียน',
        data: topBranches.map(b => b[1])
      }];

      this.$set(
        this.chartTopBranchesOptions.xaxis,
        'categories',
        topBranches.map(b => 
          b[0].length > 20 ? b[0].substring(0, 20) + '...' : b[0]
        )
      );
    },

    /**
     * ✅ FIXED: Create map data with correct Numbers
     */
    generateMapData() {
      const mapData = {};
      this.tableData.forEach(item => {
        const region = item.region_name || 'ไม่ระบุ';
        const count = this.toNumber(item.std_num);
        if (!mapData[region]) {
          mapData[region] = { count: 0, colleges: 0 };
        }
        mapData[region].count += count;
        mapData[region].colleges += 1;
      });
      this.mapData = mapData;
    },

    /**
     * ✅ FIXED: Create Top Colleges with correct Numbers
     */
    generateTopColleges() {
      const collegeMap = {};
      this.tableData.forEach(item => {
        const key = item.college_ID || item.college_code;
        const count = this.toNumber(item.std_num);
        if (!collegeMap[key]) {
          collegeMap[key] = {
            college_ID: item.college_ID,
            college_name: item.college_name,
            region_name: item.region_name,
            std_num: 0
          };
        }
        collegeMap[key].std_num += count;
      });

      this.topColleges = Object.values(collegeMap)
        .sort((a, b) => b.std_num - a.std_num)
        .slice(0, 5);
    },

    processStatsForCharts(data) {
      // Additional processing if needed
    },

    /**
     * ✅ FIXED: Correct Fetch API implementation
     * Using native Fetch instead of mixing with Axios patterns
     */
    async apiCall(action, params) {
      const payload = {
        ApiKey: 'HRvec2021',
        action: action,
        ...params
      };

      try {
        const response = await this.$http.post('api_student_branch_by_region.php', {
          payload
        });

        // ✅ Check HTTP status (200 = OK)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // ✅ Parse JSON response
        const data = await response.json();
        
        // ✅ Validate API response
        if (!data.status) {
          throw new Error(data.message || 'API returned error status');
        }

        console.log(`✅ API Call - ${action}:`, data);
        return data;
      } catch (error) {
        console.error(`❌ API Error (${action}):`, error);
        throw error;
      }
    },

    /**
     * ✅ FIXED: Proper notification display
     */
    showNotification(message, type = 'error') {
      this.snackbar = {
        show: true,
        message: message,
        color: type === 'error' ? 'red' : type === 'warning' ? 'orange' : 'green'
      };
    },

    refreshData() {
      this.loadInitialData();
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    },

    getStudentCountColor(count) {
      const num = this.toNumber(count);
      if (num > 500) return '#D32F2F';
      if (num > 300) return '#F57C00';
      if (num > 100) return '#FBC02D';
      return '#388E3C';
    },

    getRegionColor(region) {
      const colors = {
        'ภาคเหนือ': '#FF6B6B',
        'ภาคตะวันออกเฉียงเหนือ': '#4ECDC4',
        'ภาคกลาง': '#45B7D1',
        'ภาคตะวันออก': '#FFA07A',
        'ภาคตะวันตก': '#98D8C8',
        'ภาคใต้': '#F7DC6F'
      };
      return colors[region] || '#90A4AE';
    }
  }
};
</script>

<style lang="scss" scoped>
.student-dashboard {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;

  .gradient-header {
    background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;

    .text-white-70 {
      opacity: 0.9;
    }
  }

  .stat-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
    }

    .icon-stat {
      opacity: 0.15;
      margin-left: 16px;
    }

    &.stat-card-1 {
      background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%);
      border-left: 4px solid #1976D2;

      .text-gradient-1 {
        background: linear-gradient(135deg, #1976D2, #1565C0);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .icon-stat-1 {
        color: #1976D2;
      }
    }

    &.stat-card-2 {
      background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%);
      border-left: 4px solid #4CAF50;

      .text-gradient-2 {
        background: linear-gradient(135deg, #4CAF50, #388E3C);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .icon-stat-2 {
        color: #4CAF50;
      }
    }

    &.stat-card-3 {
      background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%);
      border-left: 4px solid #FF9800;

      .text-gradient-3 {
        background: linear-gradient(135deg, #FF9800, #F57C00);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .icon-stat-3 {
        color: #FF9800;
      }
    }

    &.stat-card-4 {
      background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%);
      border-left: 4px solid #9C27B0;

      .text-gradient-4 {
        background: linear-gradient(135deg, #9C27B0, #7B1FA2);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .icon-stat-4 {
        color: #9C27B0;
      }
    }
  }

  ::v-deep .v-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
}
</style>